import { permissionSets } from '@app/permissions';
import usePermissions from '@app/permissions/usePermissions';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { FeatureFlagKeys, useFeatureFlag } from '@modules/FeatureFlagModule';

/**
 * Whether the current user is entitled to texting -- the same LaunchDarkly flag
 * and permission set that gate the Messages side-nav link
 * (`MainLayout.tsx`) and the Texts route (`mainRoutes.tsx`).
 *
 * This is the feature's single imperative gate call site: it guards the hidden
 * app-lifetime count producer. `MessagesNavBadge` deliberately does not call it
 * -- the badge already renders inside `MainLayout`'s flag ternary and its
 * `RequirePermissions`, so a second check there would be a tautology. The
 * invariant worth protecting is that every gate site resolves the *same two
 * exported constants*, not that there is only one call site.
 *
 * Deriving from `useAuth().user` is load-bearing beyond entitlement:
 * `PusherModule` clears `window.pusherInstance` on sign-out and builds a new
 * client on the next sign-in, while `comm-hub-text-list` reads that global once
 * at init. Keying on auth state is what unmounts the producer on sign-out so it
 * re-reads the fresh client on sign-in.
 */
export const useTextingEntitled = (): boolean => {
  const { user } = useAuth();
  const textingFlag = useFeatureFlag(
    FeatureFlagKeys.featureContactcenterAgentInboxTexting,
  );
  const hasTextingPermission = usePermissions(
    permissionSets.general.TEXTING,
    'all',
  );

  return !!user && !!textingFlag && !!hasTextingPermission;
};

export default useTextingEntitled;
